import React, { useEffect, useRef, useState } from 'react'
import { Button, notification, Table, Typography } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import {
  Id,
  PolicyFlowClosing,
  PolicyFlowFormat,
  PolicyFlowType
} from 'src/types'
import { Tooltip } from 'src/components/Ant'
import * as PoliciesService from 'src/services/api/policies-flow'

import * as CSS from './styles'
import * as Values from './initial-values'
import sanitize from './sanitize'
import validationSchema from './validation-schema'
import FlowClosingsModal from './flow-closings-modal'

type PolicyFlowClosingList = PolicyFlowClosing & { key: Id }

export default function ClosingFlow() {
  const [dataSource, setDataSource] = useState<PolicyFlowClosingList[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  const refFormik = useRef<Formik<PolicyFlowClosing>>(null)
  const isSubscribed = useRef(true)
  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  useEffect(() => {
    setIsLoading(true)
    PoliciesService.getPolicyClosings()
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const closings: PolicyFlowClosingList[] =
              response.data?.policy_closings.map(data => ({
                ...data,
                key: data.policy_closing_id
              })) || []
            setDataSource(closings)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        //Send Bugsnag?
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }, [t])

  const columns: ColumnProps<PolicyFlowClosingList>[] = [
    {
      align: 'center',
      dataIndex: 'code',
      render: (code: string) => {
        if (code?.length > 50) {
          return <Tooltip title={code}>{`${code.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{code}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.code.title')}
        </Typography.Text>
      ),
      width: 120
    },
    {
      align: 'center',
      dataIndex: 'name',
      render: (name: string) => {
        if (name?.length > 50) {
          return <Tooltip title={name}>{`${name.substring(0, 47)}...`}</Tooltip>
        }
        return <Typography.Text>{name}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.resources.common.name.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowClosing) => {
        if (record?.cost_center_closing) {
          return (
            <Typography.Text>
              {t('web.policies-flow.type.cost-center')}
            </Typography.Text>
          )
        }

        return (
          <Typography.Text>
            {t('web.policies-flow.type.hierarchy')}
          </Typography.Text>
        )
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.closing.type.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowClosing) => {
        const { automatic_closing, consensual_closing } = record

        if (automatic_closing) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.automatic')}
            </Typography.Text>
          )
        }

        if (consensual_closing) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.consensus')}
            </Typography.Text>
          )
        }

        if (!automatic_closing && !consensual_closing) {
          return (
            <Typography.Text>
              {t('web.policies-flow.format.standard')}
            </Typography.Text>
          )
        }

        return ''
      },
      title: (
        <Typography.Text strong>
          {t('web.policies-flow.closing.format.title')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: record => (
        <CSS.Switch
          style={{ width: '100%' }}
          checked={record.active}
          onClick={() => {
            setIsLoading(true)
            PoliciesService.SavePolicyClosings({
              ...record,
              active: !record.active
            })
              .then(response => {
                if (isSubscribed.current) {
                  if (response.status === 200) {
                    const record = response.data.policy_closings[0]
                    setDataSource(data =>
                      data.map(d =>
                        d.policy_closing_id === record.policy_closing_id
                          ? { ...record, key: record.policy_closing_id }
                          : d
                      )
                    )
                  } else {
                    notification.error({
                      message: t('common.response.status-500'),
                      placement: 'bottomRight'
                    })
                  }
                }
              })
              .catch(response => {
                notification.error({
                  message: t('common.response.status-500'),
                  placement: 'bottomRight'
                })
              })
              .finally(() => {
                if (isSubscribed.current) {
                  setIsLoading(false)
                }
              })
          }}
        />
      ),
      title: <Typography.Text strong>{t('common.active')}</Typography.Text>,
      width: 100
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyFlowClosing) => (
        <Button
          icon="edit"
          type="ghost"
          onClick={() => {
            refFormik.current?.resetForm(record)

            let type = PolicyFlowType.HIERARCHY
            let format = PolicyFlowFormat.STANDARD

            if (record.automatic_closing) {
              format = PolicyFlowFormat.AUTOMATIC
            }
            if (record.consensual_closing) {
              format = PolicyFlowFormat.CONSENSUS
            }

            if (record.cost_center_closing) {
              type = PolicyFlowType.COST_CENTER
            }

            setModalPolicyFlowFormat(format)
            setModalPolicyFlowType(type)
          }}
        />
      ),
      title: (
        <Typography.Text strong>{t('common.actions.edit')}</Typography.Text>
      ),
      width: 120
    }
  ]

  function handleSubmit(values: PolicyFlowClosing) {
    setIsLoading(true)
    const sanitized = sanitize(values)
    PoliciesService.SavePolicyClosings(sanitized)
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            const record = response.data.policy_closings[0]
            if (typeof values.policy_closing_id === 'string') {
              //is new
              setDataSource(data => [
                ...data,
                { ...record, key: record.policy_closing_id }
              ])
            } else {
              //is an update
              setDataSource(data =>
                data.map(d =>
                  d.policy_closing_id === record.policy_closing_id
                    ? { ...record, key: record.policy_closing_id }
                    : d
                )
              )
            }
            refFormik.current.resetForm(Values.initialValues)
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }

  function ButtonAdd() {
    return (
      <Button
        icon="plus"
        type="primary"
        onClick={() => {
          if (refFormik.current) {
            refFormik.current.resetForm(Values.defaultValue)
            setModalPolicyFlowFormat(PolicyFlowFormat.STANDARD)
            setModalPolicyFlowType(PolicyFlowType.HIERARCHY)
          }
        }}
      >
        {t('common.actions.add')}
      </Button>
    )
  }

  return (
    <>
      <CSS.ListTitle>
        <span>{t('web.policies-flow.list-title')}</span>
        <ButtonAdd />
      </CSS.ListTitle>
      <Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={isLoading}
        locale={{
          emptyText: t('common.no-data-found')
        }}
        pagination={false}
      />
      <br />
      <CSS.ButtonContainer>
        <ButtonAdd />
      </CSS.ButtonContainer>
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={formikProps => {
          if (!formikProps.values.policy_closing_id) {
            return <span />
          }

          const isVisible = Boolean(formikProps?.values?.policy_closing_id)

          return (
            <FlowClosingsModal
              formikProps={formikProps}
              isLoading={isLoading}
              isVisible={isVisible}
              modalPolicyFlowFormat={modalPolicyFlowFormat}
              modalPolicyFlowType={modalPolicyFlowType}
              refFormik={refFormik}
              setModalPolicyFlowFormat={setModalPolicyFlowFormat}
              setModalPolicyFlowType={setModalPolicyFlowType}
            />
          )
        }}
        validationSchema={validationSchema}
      />
    </>
  )
}
